// DistributedBatchProgress.tsx - Progress monitoring component for distributed batch jobs

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Progress,
  Typography,
  Button,
  Row,
  Col,
  Statistic,
  Alert,
  Space,
  Tag,
  Tooltip,
  Modal,
  List,
  Badge,
  notification,
  Table,
  Tabs,
  Collapse,
  Input,
  Checkbox,
  Select
} from 'antd';
import {
  PauseOutlined,
  PlayCircleOutlined,
  StopOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';

import {
  BatchProgress,
  BatchProgressMonitor,
  getBatchProgress,
  getBatchResults,
  listBatches,
  ActiveBatch,
  deleteBatch,
  pruneOldBatches
} from './api_distributed';
import {
  fetchAiTestingModelCatalog,
  AITestingModelCatalogEntry,
} from './api';

import { calculateCost } from './utils';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const POLL_INTERVAL_MS = 30000;

const HISTORICAL_TIMING = {
  avgJobDurationSeconds: 747.7,
  jobsPerMinute: 0.08,
  totalElapsedMinutes: 1869,
  totalJobs: 150,
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed': return 'green';
    case 'failed': return 'red';
    case 'cancelled': return 'orange';
    case 'running': return 'blue';
    case 'queued': return 'purple';
    default: return 'default';
  }
};

type PricingRate = { input: number; output: number; cachedInput?: number };

const buildCostRatesFromCatalog = (
  catalog: AITestingModelCatalogEntry[]
): Record<string, PricingRate> => {
  const rateMap: Record<string, PricingRate> = {};
  catalog.forEach((entry) => {
    const modelId = (entry.id || '').trim();
    const apiModel = (entry.apiModel || '').trim();
    const input = Number(entry.inputCostPerMTokens);
    const output = Number(entry.outputCostPerMTokens);
    if (!modelId || !Number.isFinite(input) || input < 0 || !Number.isFinite(output) || output < 0) {
      return;
    }
    const normalized: PricingRate = { input, output };
    if (entry.cachedInputCostPerMTokens !== undefined && entry.cachedInputCostPerMTokens !== null) {
      const cached = Number(entry.cachedInputCostPerMTokens);
      if (Number.isFinite(cached) && cached >= 0) {
        normalized.cachedInput = cached;
      }
    }
    rateMap[modelId] = normalized;
    if (apiModel) {
      rateMap[apiModel] = normalized;
    }
  });
  return rateMap;
};

interface DistributedBatchProgressProps {
  batchId: string;
  onComplete?: (batchId: string, results: any[]) => void;
  autoStart?: boolean;
}

export const DistributedBatchProgress: React.FC<DistributedBatchProgressProps> = ({
  batchId,
  onComplete,
  autoStart = true
}) => {
  const [progress, setProgress] = useState<BatchProgress | null>(null);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [monitor, setMonitor] = useState<BatchProgressMonitor | null>(null);
  const [costSummary, setCostSummary] = useState<{ translation: number; evaluation: number; total: number; computedAt?: string } | null>(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costRates, setCostRates] = useState<Record<string, PricingRate>>({});
  const hasNotifiedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const firstSeenAtRef = useRef<number | null>(null);

  // Keep latest onComplete handler without re-initializing the monitor
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Initialize monitor
  useEffect(() => {
    // Reset per-batch notification guard only when batchId changes
    hasNotifiedRef.current = false;

    const progressMonitor = new BatchProgressMonitor(batchId);
    
    progressMonitor
      .onProgress((progressData) => {
        // Record first-seen timestamp (fallback start time) for wall-clock calculations
        if (!firstSeenAtRef.current) {
          const createdAt = (progressData as any)?.created_at || (progressData as any)?.started_at;
          firstSeenAtRef.current = createdAt ? Date.parse(createdAt) : Date.now();
        }
        setProgress(progressData);
        setError(null);
      })
      .onComplete((finalProgress) => {
        setProgress(finalProgress);
        setIsMonitoring(false);
        // Compute final cost summary from saved results
        computeCosts();

        // Ensure completion side-effects happen only once per batch
        if (!hasNotifiedRef.current) {
          hasNotifiedRef.current = true;

          if (finalProgress.status === 'completed' && onCompleteRef.current) {
            // Fetch results and call completion callback
            getBatchResults(batchId).then(results => {
              try {
                onCompleteRef.current?.(batchId, results);
              } catch (err) {
                console.error('onComplete callback error:', err);
              }
            }).catch(err => {
              console.error('Failed to fetch batch results:', err);
            });
          }
          
          notification.success({
            message: 'Batch Job Complete',
            description: `Batch ${batchId} has finished processing.`
          });
        }
      })
      .onError((err) => {
        setError(err.message);
        setIsMonitoring(false);
        
        notification.error({
          message: 'Batch Monitoring Error',
          description: err.message
        });
      });

    setMonitor(progressMonitor);

    // Auto-start monitoring if requested
    if (autoStart) {
      startMonitoring(progressMonitor);
    }

    // Cleanup on unmount
    return () => {
      progressMonitor.stop();
    };
  }, [batchId, autoStart]);

  useEffect(() => {
    let cancelled = false;
    const loadCostRates = async () => {
      try {
        const catalog = await fetchAiTestingModelCatalog(true);
        if (cancelled) return;
        setCostRates(buildCostRatesFromCatalog(catalog));
      } catch (error) {
        if (cancelled) return;
        notification.warning({
          message: 'Cost Catalog Unavailable',
          description: 'Unable to load model pricing. Cost totals may be incomplete.',
        });
      }
    };
    loadCostRates();
    return () => {
      cancelled = true;
    };
  }, []);

  const startMonitoring = useCallback((progressMonitor?: BatchProgressMonitor) => {
    const monitorToUse = progressMonitor || monitor;
    if (monitorToUse) {
      setIsMonitoring(true);
      setError(null);
      monitorToUse.start(POLL_INTERVAL_MS); // Poll every 10 seconds
    }
  }, [monitor]);

  const stopMonitoring = useCallback(() => {
    if (monitor) {
      monitor.stop();
      setIsMonitoring(false);
    }
  }, [monitor]);

  const computeCosts = useCallback(async () => {
    setCostLoading(true);
    try {
      const results = await getBatchResults(batchId, 10000);
      let translation = 0;
      let evaluation = 0;

      for (const r of results as any[]) {
        for (const tr of (r.translation_results || [])) {
          const ti = tr?.token_info;
          if (ti) {
            try { translation += calculateCost(tr.model, ti, costRates); } catch {}
          }
        }
        for (const er of (r.evaluation_results || [])) {
          const ti = er?.token_info;
          if (ti) {
            try {
              const parts = (er.model || '').split('::', 2);
              const evalModel = parts[1] || parts[0] || '';
              evaluation += calculateCost(evalModel, ti, costRates);
            } catch {}
          }
        }
      }

      setCostSummary({
        translation,
        evaluation,
        total: translation + evaluation,
        computedAt: new Date().toISOString()
      });
    } catch (error) {
      notification.error({
        message: 'Cost Summary Error',
        description: (error as Error).message
      });
    } finally {
      setCostLoading(false);
    }
  }, [batchId, costRates]);

  const refreshProgress = async () => {
    try {
      const latestProgress = await getBatchProgress(batchId);
      setProgress(latestProgress);
      setError(null);
    } catch (error) {
      setError((error as Error).message);
    }
  };


  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleOutlined />;
      case 'failed': return <ExclamationCircleOutlined />;
      case 'cancelled': return <StopOutlined />;
      case 'running': return <PlayCircleOutlined />;
      case 'queued': return <ClockCircleOutlined />;
      default: return null;
    }
  };

  const formatTimeEstimate = (minutes?: number): string => {
    if (minutes === undefined || minutes === null) return 'Calculating...';
    if (minutes <= 0) return '0 minutes';
    if (minutes < 1) return 'Less than 1 minute';
    if (minutes < 60) return `${Math.round(minutes)} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours === 0) return `${mins} minutes`;
    if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours}h ${mins}m`;
  };

  // Derived fields to make UI resilient to backend mapping/timing
  const derivedStatus = React.useMemo(() => {
    if (!progress) return 'queued';
    let s = progress.status;
    if (s === 'queued' && progress.running_jobs > 0) s = 'running';
    if (progress.total_jobs > 0 && (progress.completed_jobs + progress.failed_jobs) >= progress.total_jobs) s = 'completed';
    return s;
  }, [progress]);

  const derivedPct = React.useMemo(() => {
    if (!progress) return 0;
    const pct = (progress as any).progress_percentage ?? 0;
    if (typeof pct === 'number' && pct > 0) return pct;
    if (progress.total_jobs > 0) {
      return Math.round((progress.completed_jobs / progress.total_jobs) * 1000) / 10;
    }
    return 0;
  }, [progress]);

  const effectiveLastUpdated = React.useMemo(() => {
    if (!progress) return null;
    return (progress as any).last_updated || (progress as any).updated_at || null;
  }, [progress]);

  // Derived timing metrics with historical fallbacks
  const derivedTimings = React.useMemo(() => {
    if (!progress) return null;
    const jt = ((progress as any)?.job_timings || {}) as any;

    // Establish wall-clock start and end
    const createdAtStr = (progress as any)?.created_at || (progress as any)?.started_at;
    const lastUpdatedStr = (progress as any)?.last_updated || (progress as any)?.updated_at;
    const endMs = lastUpdatedStr ? Date.parse(lastUpdatedStr) : Date.now();
    const startMs = createdAtStr
      ? Date.parse(createdAtStr)
      : (firstSeenAtRef.current ?? endMs);
    const wallClockSeconds = Math.max(0, Math.round((endMs - startMs) / 1000));

    const completedOrFailed = (progress.completed_jobs || 0) + (progress.failed_jobs || 0);
    const remainingJobs = Math.max(0, (progress.total_jobs || 0) - completedOrFailed);
    const isComplete = (progress.total_jobs || 0) > 0 && remainingJobs === 0;

    // Prefer backend throughput; else compute from wall clock; else fallback historical
    const backendJPM = (typeof jt.jobs_per_minute === 'number' && jt.jobs_per_minute > 0) ? jt.jobs_per_minute : undefined;
    const observedJPM = wallClockSeconds > 0 ? (completedOrFailed / (wallClockSeconds / 60)) : 0;
    const jobsPerMinute: number = backendJPM ?? (observedJPM > 0 ? observedJPM : HISTORICAL_TIMING.jobsPerMinute);

    // Prefer backend avg duration; else compute from wall clock; else fallback historical
    const backendAvg = (typeof jt.avg_job_duration_seconds === 'number' && jt.avg_job_duration_seconds > 0) ? jt.avg_job_duration_seconds : undefined;
    const observedAvg = completedOrFailed > 0 ? (wallClockSeconds / completedOrFailed) : 0;
    const avgJobDurationSeconds: number = backendAvg ?? (observedAvg > 0 ? observedAvg : HISTORICAL_TIMING.avgJobDurationSeconds);

    // Remaining time: clamp to 0 on completion
    let estimated_remaining_minutes: number;
    if (isComplete) {
      estimated_remaining_minutes = 0;
    } else if (typeof jt.estimated_remaining_minutes === 'number' && jt.estimated_remaining_minutes >= 0) {
      estimated_remaining_minutes = jt.estimated_remaining_minutes;
    } else if (jobsPerMinute > 0) {
      estimated_remaining_minutes = remainingJobs / jobsPerMinute;
    } else {
      estimated_remaining_minutes = (remainingJobs * avgJobDurationSeconds) / 60;
    }

    // Wall-clock elapsed, not sum of job durations
    const total_duration_seconds: number = wallClockSeconds;

    return {
      jobsPerMinute,
      avgJobDurationSeconds,
      estimated_remaining_minutes,
      total_duration_seconds,
    };
  }, [progress]);

  if (!progress && !error) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Title level={4}>Loading batch progress...</Title>
        </div>
      </Card>
    );
  }

  // Render detailed progress tables
  const renderTextProgress = () => {
    if (!progress || !(progress as any).text_progress) return null;
    
    const textProgress = (progress as any).text_progress || {};
    const dataSource = Object.entries(textProgress).map(([textId, data]: [string, any]) => ({
      key: textId,
      name: data.name,
      completed: data.completed_runs,
      total: data.total_runs,
      failed: data.failed_runs,
      progress: data.total_runs > 0 ? (data.completed_runs / data.total_runs) * 100 : 0,
      models: data.model_status
    }));

    return (
      <Table
        dataSource={dataSource}
        size="small"
        pagination={false}
        columns={[
          {
            title: 'Text Name',
            dataIndex: 'name',
            key: 'name',
            width: '30%'
          },
          {
            title: 'Progress',
            key: 'progress',
            width: '30%',
            render: (_, record) => (
              <Progress 
                percent={Math.round(record.progress)} 
                size="small"
                status={record.failed > 0 ? 'exception' : undefined}
              />
            )
          },
          {
            title: 'Runs',
            key: 'runs',
            render: (_, record) => (
              <Space>
                <Tag color="green">{record.completed}/{record.total}</Tag>
                {record.failed > 0 && <Tag color="red">Failed: {record.failed}</Tag>}
              </Space>
            )
          },
          {
            title: 'Translations',
            key: 'translations',
            render: (_, record) => {
              const entries = Object.values(record.models || {}) as any[];
              const total = entries.reduce((sum, s: any) => sum + (s.total || 0), 0);
              const completed = entries.reduce((sum, s: any) => sum + (s.completed || 0), 0);
              const inprog = entries.reduce((sum, s: any) => sum + (s.in_progress || 0), 0);
              return (
                <Space>
                  <Tag color="green">Done: {completed}</Tag>
                  {inprog > 0 && <Tag color="blue">In Prog: {inprog}</Tag>}
                  <Tag>Total: {total}</Tag>
                </Space>
              );
            }
          },
          {
            title: 'Evaluations',
            key: 'evaluations',
            render: (_, record) => {
              const entries = Object.values(record.models || {}) as any[];
              const evalDone = entries.reduce((sum, s: any) => sum + (s.eval_completed || 0), 0);
              const evalPlan = entries.reduce((sum, s: any) => sum + (s.eval_planned || 0), 0);
              return (
                <Space>
                  <Tag color="cyan">Completed: {evalDone}</Tag>
                  <Tag>Planned: {evalPlan}</Tag>
                </Space>
              );
            }
          },
          {
            title: 'Models',
            key: 'models',
            render: (_, record) => {
              const modelEntries = Object.entries(record.models || {});
              if (modelEntries.length === 0) return '-';
              return (
                <Tooltip title={
                  <div>
                    {modelEntries.map(([model, status]: [string, any]) => (
                      <div key={model} style={{ marginBottom: 6 }}>
                        <div><strong>{model}</strong></div>
                        <div>
                          Translations: {status.completed || 0}/{status.total || 0}
                          {(status.in_progress || 0) > 0 && <> • In Progress: {status.in_progress}</>}
                        </div>
                        <div>
                          Evaluations: {(status.eval_completed || 0)}/{(status.eval_planned || 0)}
                        </div>
                      </div>
                    ))}
                  </div>
                }>
                  <Text>{modelEntries.length} models</Text>
                </Tooltip>
              );
            }
          }
        ]}
      />
    );
  };

  const renderModelProgress = () => {
    if (!progress || !(progress as any).model_progress) return null;
    
    const modelProgress = (progress as any).model_progress || {};
    const dataSource = Object.entries(modelProgress).map(([model, data]: [string, any]) => ({
      key: model,
      model,
      ...data
    }));

    return (
      <Table
        dataSource={dataSource}
        size="small"
        pagination={false}
        columns={[
          {
            title: 'Model',
            dataIndex: 'model',
            key: 'model',
            width: '25%'
          },
          {
            title: 'Completed',
            dataIndex: 'completed',
            key: 'completed',
            render: (val) => <Tag color="green">{val || 0}</Tag>
          },
          {
            title: 'Failed',
            dataIndex: 'failed',
            key: 'failed',
            render: (val) => val > 0 ? <Tag color="red">{val}</Tag> : <Tag>{val || 0}</Tag>
          },
          {
            title: 'In Progress',
            dataIndex: 'in_progress',
            key: 'in_progress',
            render: (val) => val > 0 ? <Tag color="blue">{val}</Tag> : <Tag>{val || 0}</Tag>
          },
          {
            title: 'Eval Completed',
            dataIndex: 'eval_completed',
            key: 'eval_completed',
            render: (val) => <Tag color="cyan">{val || 0}</Tag>
          },
          {
            title: 'Eval Planned',
            dataIndex: 'eval_planned',
            key: 'eval_planned',
            render: (val) => <Tag>{val || 0}</Tag>
          },
          {
            title: 'Total Jobs',
            dataIndex: 'total_jobs',
            key: 'total_jobs',
            render: (val) => <Tag>{val || 0}</Tag>
          },
          {
            title: 'Avg Time',
            dataIndex: 'avg_time_seconds',
            key: 'avg_time',
            render: (val) => val ? `${val}s` : '-'
          },
          {
            title: 'Success Rate',
            dataIndex: 'success_rate',
            key: 'success_rate',
            render: (val) => val !== undefined ? (
              <Progress 
                percent={Math.round(val * 100)} 
                size="small" 
                format={() => `${Math.round(val * 100)}%`}
              />
            ) : '-'
          }
        ]}
      />
    );
  };

  const renderRunProgress = () => {
    if (!progress || !(progress as any).run_progress) return null;
    
    const runProgress = (progress as any).run_progress || {};
    const dataSource = Object.entries(runProgress).map(([runNum, data]: [string, any]) => ({
      key: runNum,
      run: `Run ${runNum}`,
      ...data
    }));

    return (
      <Table
        dataSource={dataSource}
        size="small"
        pagination={false}
        columns={[
          {
            title: 'Run',
            dataIndex: 'run',
            key: 'run',
            width: '20%'
          },
          {
            title: 'Progress',
            key: 'progress',
            width: '40%',
            render: (_, record) => (
              <Progress 
                percent={Math.round(record.percentage || 0)} 
                size="small"
              />
            )
          },
          {
            title: 'Texts Completed',
            key: 'texts',
            render: (_, record) => (
              <Tag>{record.completed_texts}/{record.total_texts}</Tag>
            )
          }
        ]}
      />
    );
  };

  return (
    <Card 
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Title level={4} style={{ margin: 0 }}>
                Batch Progress
              </Title>
              <Text type="secondary">({batchId.slice(0, 8)}...)</Text>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={refreshProgress}
                size="small"
              >
                Refresh
              </Button>
              {!isMonitoring && progress?.status !== 'completed' && progress?.status !== 'failed' && (
                <Button
                  icon={<PlayCircleOutlined />}
                  onClick={() => startMonitoring()}
                  size="small"
                  type="primary"
                >
                  Start Monitoring
                </Button>
              )}
              {isMonitoring && (
                <Button
                  icon={<PauseOutlined />}
                  onClick={stopMonitoring}
                  size="small"
                >
                  Stop Monitoring
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      }
      style={{ marginBottom: '16px' }}
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {progress && (
        <Space direction="vertical" style={{ width: '100%' }}>
          {/* Status Row */}
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="Status"
                  value={derivedStatus.toUpperCase()}
                  prefix={getStatusIcon(derivedStatus)}
                  valueStyle={{ color: getStatusColor(derivedStatus) === 'green' ? '#52c41a' : 
                                       getStatusColor(derivedStatus) === 'red' ? '#ff4d4f' : 
                                       getStatusColor(derivedStatus) === 'blue' ? '#1890ff' : 'var(--text-main)' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="Progress"
                  value={derivedPct}
                  suffix="%"
                  precision={1}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="Jobs Completed"
                  value={progress.completed_jobs}
                  suffix={`/ ${progress.total_jobs}`}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="Time Remaining"
                  value={formatTimeEstimate((derivedTimings as any)?.estimated_remaining_minutes)}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {((derivedTimings as any)?.jobsPerMinute ?? 0).toFixed(2)} jobs/min
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Progress Bar */}
          <Progress
            percent={derivedPct}
            status={derivedStatus === 'failed' ? 'exception' : 
                   derivedStatus === 'completed' ? 'success' : 'active'}
            format={() => `${progress.completed_jobs} / ${progress.total_jobs} jobs`}
            strokeWidth={20}
          />

          {/* Job Breakdown */}
          <Row gutter={[8, 8]}>
            <Col>
              <Tag color="green" icon={<CheckCircleOutlined />}>
                Completed: {progress.completed_jobs}
              </Tag>
            </Col>
            <Col>
              <Tag color="blue" icon={<PlayCircleOutlined />}>
                Running: {progress.running_jobs}
              </Tag>
            </Col>
            <Col>
              <Tag color="default" icon={<ClockCircleOutlined />}>
                Pending: {progress.pending_jobs}
              </Tag>
            </Col>
            {progress.failed_jobs > 0 && (
              <Col>
                <Tag color="red" icon={<ExclamationCircleOutlined />}>
                  Failed: {progress.failed_jobs}
                </Tag>
              </Col>
            )}
            {progress.results_available > 0 && (
              <Col>
                <Tag color="cyan" icon={<EyeOutlined />}>
                  Results Available: {progress.results_available}
                </Tag>
              </Col>
            )}
          </Row>

          {/* Detailed Progress Tabs */}
          <Tabs defaultActiveKey="overview" style={{ marginTop: 16 }}>
            <TabPane tab="Overview" key="overview">
              <Row justify="space-between" align="middle">
                <Col>
                  <Space>
                    <Badge 
                      status={isMonitoring ? "processing" : "default"} 
                      text={isMonitoring ? "Live monitoring active" : "Monitoring stopped"}
                    />
                    <Text type="secondary">
                      Last updated: {effectiveLastUpdated ? new Date(effectiveLastUpdated as any).toLocaleTimeString() : '—'}
                    </Text>
                  </Space>
                </Col>
                <Col>
                  <Space>
                    <Button size="small" onClick={computeCosts} loading={costLoading}>
                      {costSummary ? 'Refresh Costs' : 'Compute Costs'}
                    </Button>
                    {costSummary?.computedAt && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Computed {new Date(costSummary.computedAt).toLocaleTimeString()}
                      </Text>
                    )}
                  </Space>
                </Col>
              </Row>
            </TabPane>
            
            <TabPane tab="Text Progress" key="texts">
              <Title level={5}>Progress by Text</Title>
              {renderTextProgress()}
            </TabPane>
            
            <TabPane tab="Model Performance" key="models">
              <Title level={5}>Performance by Model</Title>
              {renderModelProgress()}
            </TabPane>
            
            <TabPane tab="Run Progress" key="runs">
              <Title level={5}>Progress by Run</Title>
              {renderRunProgress()}
            </TabPane>
            
            <TabPane tab="Timing Details" key="timing">
              <Title level={5}>Job Timing Statistics</Title>
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Card size="small">
                    <Statistic
                      title="Avg Job Duration"
                      value={(derivedTimings?.avgJobDurationSeconds ?? 0)}
                      suffix="seconds"
                      precision={1}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small">
                    <Statistic
                      title="Jobs per Minute"
                      value={(derivedTimings?.jobsPerMinute ?? 0)}
                      precision={2}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small">
                    <Statistic
                      title="Total Time Elapsed"
                      value={Math.round(((derivedTimings?.total_duration_seconds ?? 0) / 60))}
                      suffix="minutes"
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small">
                    <Statistic
                      title="Est. Time Remaining"
                      value={formatTimeEstimate(derivedTimings?.estimated_remaining_minutes)}
                    />
                  </Card>
                </Col>
            </Row>
            {costSummary && (
              <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Translation Cost (Batch)"
                      value={costSummary.translation}
                      prefix="$"
                      precision={5}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Evaluation Cost (Batch)"
                      value={costSummary.evaluation}
                      prefix="$"
                      precision={5}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Total Cost (Batch)"
                      value={costSummary.total}
                      prefix="$"
                      precision={5}
                    />
                  </Card>
                </Col>
              </Row>
            )}
            </TabPane>
          </Tabs>
        </Space>
      )}
    </Card>
  );
};

// Component for listing all active batches
interface ActiveBatchesProps {
  onSelectBatch?: (batchId: string) => void;
}

export const ActiveBatchesList: React.FC<ActiveBatchesProps> = ({ onSelectBatch }) => {
  const [batches, setBatches] = useState<ActiveBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [manualBatches, setManualBatches] = useState<ActiveBatch[]>([]);
  const [searchId, setSearchId] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'all'>('all');

  const loadActiveBatches = async () => {
    setLoading(true);
    try {
      const items = await listBatches(100, statusFilter);
      setBatches(items);
    } catch (error) {
      notification.error({
        message: 'Failed to Load Batches',
        description: (error as Error).message
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActiveBatches();
    // Refresh every 30 seconds, re-bind on filter change
    const interval = setInterval(loadActiveBatches, 30000);
    return () => clearInterval(interval);
  }, [statusFilter]);

  const handleDeleteBatch = async (batchId: string, opts?: { deleteResults?: boolean; deleteJobs?: boolean }) => {
    try {
      await deleteBatch(batchId, opts);
      notification.success({
        message: 'Batch Deleted',
        description: `Batch ${batchId.slice(0, 8)}... has been removed`
      });
      // Remove from manual list if present
      setManualBatches(prev => prev.filter(b => b.batch_id !== batchId));
      await loadActiveBatches();
    } catch (error) {
      notification.error({
        message: 'Delete Failed',
        description: (error as Error).message
      });
    }
  };

  const handlePruneOld = async () => {
    Modal.confirm({
      title: 'Prune old batches',
      icon: <ExclamationCircleOutlined />,
      content: 'This will delete batch status records older than 30 days. Related results and job status will NOT be deleted.',
      okText: 'Prune 30d',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const res = await pruneOldBatches(30, { deleteResults: false, deleteJobs: false });
          notification.success({
            message: 'Prune Complete',
            description: `Deleted ${res.deleted_batches} batches older than ${res.older_than_days} days`
          });
          await loadActiveBatches();
        } catch (error) {
          notification.error({
            message: 'Prune Failed',
            description: (error as Error).message
          });
        }
      }
    });
  };

  // Map a BatchProgress response into ActiveBatch shape for display/search results
  const mapProgressToActive = (batchId: string, data: any): ActiveBatch => ({
    batch_id: batchId,
    status: data.status,
    progress_percentage: data.progress_percentage || 0,
    total_jobs: data.total_jobs || 0,
    completed_jobs: data.completed_jobs || 0,
    failed_jobs: data.failed_jobs || 0,
    created_at: data.created_at ? new Date(data.created_at as any).toISOString() : new Date().toISOString(),
    config: {
      test_name: (data.config && (data.config.test_name || data.config.name)) || batchId,
      target_language: (data.config && data.config.target_language) || '-',
      num_texts: (data.config && (data.config.num_texts || (data.text_progress ? Object.keys(data.text_progress).length : 0))) || 0,
      num_runs: (data.config && data.config.num_runs) || 0,
      translation_models: (data.config && data.config.translation_models) || [],
      evaluation_models: (data.config && data.config.evaluation_models) || [],
      proofreader_enabled: Boolean(data.config && data.config.proofreader_enabled),
      proofreader_formality_model: (data.config && data.config.proofreader_formality_model) || undefined,
      proofreader_general_model: (data.config && data.config.proofreader_general_model) || undefined,
      proofreader_alignment_model: (data.config && data.config.proofreader_alignment_model) || undefined,
    },
  });

  const searchByBatchId = async (id: string) => {
    const trimmed = id.trim();
    if (!trimmed) return;
    setSearchLoading(true);
    try {
      const prog = await getBatchProgress(trimmed);
      const mapped = mapProgressToActive(trimmed, prog as any);
      setManualBatches(prev => {
        const dedup = prev.filter(b => b.batch_id !== trimmed);
        return [mapped, ...dedup];
      });
      notification.success({
        message: 'Batch Loaded',
        description: `Loaded batch ${trimmed.slice(0, 8)}...`
      });
    } catch (error) {
      notification.error({
        message: 'Batch Not Found',
        description: (error as Error).message
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const showDeleteOptions = (batchId: string) => {
    let delResults = false;
    let delJobs = false;
    Modal.confirm({
      title: 'Delete this batch?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <Space direction="vertical">
          <Text>This removes the batch status record.</Text>
          <Checkbox onChange={e => (delResults = e.target.checked)}>Also delete related test results</Checkbox>
          <Checkbox onChange={e => (delJobs = e.target.checked)}>Also delete related job_status records</Checkbox>
        </Space>
      ),
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        await handleDeleteBatch(batchId, { deleteResults: delResults, deleteJobs: delJobs });
      }
    });
  };
  
  return (
    <Card
      title="Batches"
      extra={
        <Space>
          <Input
            placeholder="Batch ID"
            size="small"
            value={searchId}
            onChange={e => setSearchId(e.target.value)}
            style={{ width: 220 }}
          />
          <Button
            onClick={() => searchByBatchId(searchId)}
            loading={searchLoading}
            size="small"
            type="primary"
          >
            Load
          </Button>
          <Select
            size="small"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 140 }}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Queued', value: 'queued' },
              { label: 'Running', value: 'running' },
              { label: 'Completed', value: 'completed' },
              { label: 'Failed', value: 'failed' },
              { label: 'Cancelled', value: 'cancelled' },
            ]}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={loadActiveBatches}
            loading={loading}
            size="small"
          >
            Refresh
          </Button>
          <Button
            icon={<ExclamationCircleOutlined />}
            onClick={handlePruneOld}
            size="small"
            danger
          >
            Prune 30d
          </Button>
        </Space>
      }
    >
      <List
        dataSource={[...manualBatches, ...batches]}
        renderItem={(batch) => (
            <List.Item
              actions={[
                <Button 
                  type="link" 
                  onClick={() => onSelectBatch?.(batch.batch_id)}
                  size="small"
                >
                  Monitor
                </Button>,
                <Button type="link" danger size="small"
                  disabled={!['completed', 'failed', 'cancelled', 'canceled'].includes(batch.status)}
                  onClick={() => showDeleteOptions(batch.batch_id)}>
                  Delete
                </Button>
              ]}
            >
            <List.Item.Meta
              title={
                <Space>
                  <Text>{batch.config.test_name}</Text>
                  <Tag color={getStatusColor(batch.status)}>
                    {batch.status.toUpperCase()}
                  </Tag>
                </Space>
              }
              description={
                <Space direction="vertical" size="small">
                  <Text type="secondary">
                    {batch.config.target_language} • {batch.config.num_texts} texts • {batch.config.num_runs} runs
                  </Text>
                  <Text type="secondary">
                    Proofreader: {batch.config.proofreader_enabled ? 'Enabled' : 'Disabled'}
                  </Text>
                  {(batch.config.proofreader_formality_model || batch.config.proofreader_general_model || batch.config.proofreader_alignment_model) && (
                    <Space size={[4, 4]} wrap>
                      {batch.config.proofreader_formality_model && (
                        <Tag color="blue">Formality: {batch.config.proofreader_formality_model}</Tag>
                      )}
                      {batch.config.proofreader_general_model && (
                        <Tag color="geekblue">General: {batch.config.proofreader_general_model}</Tag>
                      )}
                      {batch.config.proofreader_alignment_model && (
                        <Tag color="purple">Alignment: {batch.config.proofreader_alignment_model}</Tag>
                      )}
                    </Space>
                  )}
                  <Progress 
                    percent={batch.progress_percentage} 
                    size="small" 
                    format={() => `${batch.completed_jobs}/${batch.total_jobs}`}
                  />
                  <Text type="secondary">
                    Started: {new Date(batch.created_at).toLocaleString()}
                  </Text>
                </Space>
              }
            />
          </List.Item>
        )}
        locale={{ emptyText: 'No batches' }}
      />
    </Card>
  );
};
